import moment from 'moment';
import {
  callApi,
  compareEventsByStartTime,
  getBookedShorexItems,
  getShorexPutData,
  getRemoveShorexData,
} from '../../utils';
import {
  REQUEST_AVAILABLE_ARRIVAL_TIME_SLOTS,
  RECEIVE_AVAILABLE_ARRIVAL_TIME_SLOTS,
  SET_AVAILABLE_ARRIVAL_TIME_SLOTS_FAILURE,
  CLEAR_ARRIVAL_TIME_ERROR_MESSAGE,
  SET_SELECTED_ARRIVAL_TIME,
  REQUEST_ADDING_ARRIVAL_TIME_SLOT,
  RECEIVE_ADDING_ARRIVAL_TIME_SLOT,
  SET_ADDING_ARRIVAL_TIME_SLOT_FAILURE,
  REQUEST_REMOVE_ARRIVAL_TIME_SLOT,
  RECEIVE_REMOVE_ARRIVAL_TIME_SLOT,
  SET_REMOVE_ARRIVAL_TIME_SLOT_FAILURE,
  SET_ARRIVAL_PAGE_WAS_BOOKED,
} from '../constants/arrivalTime';

const getSelectedArrivalTimeSlotIdIsStillExists = (arrivalTimeSlots, selectedArrivalSlotId) => arrivalTimeSlots
  .some(({ code }) => parseInt(code, 10) === parseInt(selectedArrivalSlotId, 10));

const getArrivalSlots = ({ Units = [] }, arrivalDate) => {
  const [firstUnit = {}] = Units;
  const { Shorexes = [] } = firstUnit;
  return Shorexes
    .filter(({ ShorexItem = {}, AvailableDateTimes = [] }) => {
      const { code } = ShorexItem;
      const [arrivalString] = AvailableDateTimes;
      return code === 'ARRIVAL TIMES' && moment(arrivalString).isSame(arrivalDate, 'day');
    })
    .map(({ AvailableDateTimes = [], QuantityAvailable, ShorexItem = {}, SelectedGuestMask }) => ({
      startTime: moment(AvailableDateTimes[0]).format('kk:mm'),
      endTime: moment(AvailableDateTimes[0]).add(60, 'minutes').format('kk:mm'),
      remainingCapacity: QuantityAvailable,
      code: parseInt(ShorexItem.id, 10),
      duration: 60,
      selectedGuestMask: SelectedGuestMask,
    }))
    .sort(compareEventsByStartTime);
};

export const setSelectedArrivalTime = selectedArrivalSlotId => ({
  type: SET_SELECTED_ARRIVAL_TIME,
  selectedArrivalSlotId,
});

const requestAvailableArrivalTimeSlots = () => ({
  type: REQUEST_AVAILABLE_ARRIVAL_TIME_SLOTS,
});

const receiveAvailableArrivalTimeSlots = (arrivalTimeSlots, bookedSlot, bookedShorexItems, isCalledByAnotherAction) => ({
  type: RECEIVE_AVAILABLE_ARRIVAL_TIME_SLOTS,
  arrivalTimeSlots,
  bookedSlot,
  bookedShorexItems,
  isCalledByAnotherAction,
});

const setAvailableArrivalTimeSlotsFailure = error => ({
  type: SET_AVAILABLE_ARRIVAL_TIME_SLOTS_FAILURE,
  error,
});

export const clearArrivalTimeError = () => ({
  type: CLEAR_ARRIVAL_TIME_ERROR_MESSAGE,
});

export const getAvailableArrivalTimeSlots = (isCalledByAnotherAction = false) => async (dispatch, getState) => {
  dispatch(requestAvailableArrivalTimeSlots());
  const { config, bookingSearch } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: subscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
  } = config;
  const { resId } = bookingSearch;
  try {
    const { data = {} } = await callApi(
      {
        method: 'get',
        url: `/common/account/booking/${resId}/shorex`,
      },
      bookingPrefix,
      subscriptionKey,
      timeout,
    );
    const { Data = [] } = data;
    const arrivalTimeSlots = getArrivalSlots(Data, bookingSearch.arrivalDate);
    const bookedShorexItems = getBookedShorexItems(Data);
    const [bookedSlot] = arrivalTimeSlots.filter(({ selectedGuestMask }) => !!selectedGuestMask);
    dispatch(receiveAvailableArrivalTimeSlots(arrivalTimeSlots, bookedSlot, bookedShorexItems, isCalledByAnotherAction));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setAvailableArrivalTimeSlotsFailure(error));
  }
};

const requestAddingArrivalTimeSlot = () => ({
  type: REQUEST_ADDING_ARRIVAL_TIME_SLOT,
});

const receiveAddingArrivalTimeSlot = selectedArrivalSlotId => ({
  type: RECEIVE_ADDING_ARRIVAL_TIME_SLOT,
  bookedArrivalSlotId: selectedArrivalSlotId,
});

const setAddingArrivalTimeSlotFailure = error => ({
  type: SET_ADDING_ARRIVAL_TIME_SLOT_FAILURE,
  error,
});

export const addArrivalTimeSlotToBooking = () => async (dispatch, getState) => {
  dispatch(requestAddingArrivalTimeSlot());
  const { config, bookingSearch, arrivalTime } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: subscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
    REACT_APP_SHOREX_ENHANCEMENT_ENABLED: shorexEnhancementEnabled,
  } = config;
  const isShorexEnhancementEnabled = shorexEnhancementEnabled === 'true';
  const { resId, numOfUnits } = bookingSearch;
  const { selectedArrivalSlotId, bookedShorexItems, arrivalTimeSlots, bookedArrivalSlotId } = arrivalTime;
  if (!getSelectedArrivalTimeSlotIdIsStillExists(arrivalTimeSlots, selectedArrivalSlotId)) {
    dispatch(setAddingArrivalTimeSlotFailure('it_does_not_exist_anymore'));
    return;
  }
  try {
    const baseUrl = `/common/account/booking/${resId}/shorex`;
    const url = isShorexEnhancementEnabled ? `${baseUrl}?replaceAll=false` : baseUrl;
    const shorexPutData = getShorexPutData(selectedArrivalSlotId, numOfUnits, bookedShorexItems, isShorexEnhancementEnabled, bookedArrivalSlotId);
    await callApi(
      {
        method: 'PUT',
        url,
        data: shorexPutData,
      },
      bookingPrefix,
      subscriptionKey,
      timeout,
    );
    dispatch(receiveAddingArrivalTimeSlot(selectedArrivalSlotId));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setAddingArrivalTimeSlotFailure(error));
  }
};

const requestRemoveArrivalTimeSlot = () => ({
  type: REQUEST_REMOVE_ARRIVAL_TIME_SLOT,
});

const receiveRemoveArrivalTimeSlot = () => ({
  type: RECEIVE_REMOVE_ARRIVAL_TIME_SLOT,
});

const setRemoveArrivalTimeSlotFailure = error => ({
  type: SET_REMOVE_ARRIVAL_TIME_SLOT_FAILURE,
  error,
});

export const removeArrivalTimeSlotFromBooking = () => async (dispatch, getState) => {
  dispatch(requestRemoveArrivalTimeSlot());
  const { config, bookingSearch, arrivalTime } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: subscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
    REACT_APP_SHOREX_ENHANCEMENT_ENABLED: shorexEnhancementEnabled,
  } = config;
  const isShorexEnhancementEnabled = shorexEnhancementEnabled === 'true';
  const { resId, numOfUnits } = bookingSearch;
  const { bookedShorexItems, arrivalTimeSlots, bookedArrivalSlotId } = arrivalTime;
  if (!getSelectedArrivalTimeSlotIdIsStillExists(arrivalTimeSlots, bookedArrivalSlotId)) {
    dispatch(setRemoveArrivalTimeSlotFailure('it_does_not_exist_anymore'));
    return;
  }
  const shorexPutData = getShorexPutData(bookedArrivalSlotId, numOfUnits, bookedShorexItems, isShorexEnhancementEnabled);
  const removeShorexData = getRemoveShorexData(shorexPutData, bookedArrivalSlotId, isShorexEnhancementEnabled);
  try {
    const baseUrl = `/common/account/booking/${resId}/shorex`;
    const url = isShorexEnhancementEnabled ? `${baseUrl}?replaceAll=false` : baseUrl;
    await callApi(
      {
        method: 'PUT',
        url,
        data: removeShorexData,
      },
      bookingPrefix,
      subscriptionKey,
      timeout,
    );
    dispatch(receiveRemoveArrivalTimeSlot());
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setRemoveArrivalTimeSlotFailure(error));
  }
};

export const setArrivalPageWasBooked = wasBooked => ({
  type: SET_ARRIVAL_PAGE_WAS_BOOKED,
  wasBooked,
});
